import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Sélectionner...",
  searchPlaceholder = "Rechercher...",
  getOptionLabel = (option) => option?.nom || "",
  getOptionValue = (option) => option?._id,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const containerRef = useRef(null); // le champ (trigger)
  const dropdownRef = useRef(null);  // le dropdown en portal

  // Calcule la position du dropdown par rapport au champ
  const updateCoords = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();

    setCoords({
      top: rect.bottom + 4, // 4px d'espace, comme le mt-1 d'origine
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open]);

  // Recalcule si on scroll/redimensionne pendant que c'est ouvert
  useEffect(() => {
    if (!open) return;

    const handleReposition = () => updateCoords();

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  // Fermer lorsqu'on clique à l'extérieur (champ ET dropdown en portal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedTrigger = containerRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedValues = (value || []).map((id) => String(id));

  const filteredOptions = options.filter((option) => {
    const label = String(getOptionLabel(option) || "").toLowerCase();

    return label.includes(search.toLowerCase());
  });

  const toggleOption = (option) => {
    const id = String(getOptionValue(option));

    if (!id) return;

    const exists = selectedValues.includes(id);

    if (exists) {
      onChange(selectedValues.filter((item) => item !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  const removeOption = (id) => {
    onChange(selectedValues.filter((item) => item !== String(id)));
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectedOptions = options.filter((option) =>
    selectedValues.includes(String(getOptionValue(option))),
  );

const dropdownContent = (
  <div
    ref={dropdownRef}
    className="bg-white border rounded shadow-sm"
    style={{
      position: "fixed",
      top: coords.top,
      left: coords.left,
      width: coords.width,
      zIndex: 10050, // au-dessus de .adminModalOverlay (9999)
      maxHeight: "300px",
      overflow: "hidden",
    }}
  >
      {/* RECHERCHE */}
      <div className="p-2 border-bottom bg-white">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      </div>

      {/* TOUS */}
      <div
        className="px-3 py-2 border-bottom"
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          clearAll();
        }}
      >
        <span
          className={
            selectedValues.length === 0
              ? "fw-semibold text-primary"
              : "text-muted"
          }
        >
          Toutes / Tous
        </span>

        {selectedValues.length === 0 && (
          <span className="float-end text-primary">✓</span>
        )}
      </div>

      {/* OPTIONS */}
      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
        {filteredOptions.length === 0 ? (
          <div className="text-muted text-center py-3">Aucun résultat</div>
        ) : (
          filteredOptions.map((option) => {
            const id = String(getOptionValue(option));
            const label = getOptionLabel(option);
            const checked = selectedValues.includes(id);

            return (
              <div
                key={id}
                className="px-3 py-2 d-flex align-items-center"
                style={{
                  cursor: "pointer",
                  backgroundColor: checked
                    ? "rgba(13, 110, 253, 0.08)"
                    : "transparent",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOption(option);
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={checked}
                  onChange={() => {}}
                />

                <span>{label}</span>
              </div>
            );
          })
        )}
      </div>

      {/* FOOTER */}
      <div className="border-top px-3 py-2 bg-light">
        <small className="text-muted">
          {selectedValues.length === 0
            ? "Tous sélectionnés"
            : `${selectedValues.length} élément(s) sélectionné(s)`}
        </small>
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="position-relative"
      style={{ width: "100%" }}
    >
      {/* SELECT (trigger) */}
      <div
        className={`form-control d-flex align-items-center flex-wrap gap-1 ${
          open ? "border-primary" : ""
        }`}
        style={{
          minHeight: "38px",
          height: "auto",
          cursor: "pointer",
          paddingRight: "40px",
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-muted">{placeholder}</span>
        ) : (
          selectedOptions.map((option) => {
            const id = String(getOptionValue(option));

            return (
              <span
                key={id}
                className="badge bg-primary d-flex align-items-center gap-1"
              >
                {getOptionLabel(option)}

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{
                    fontSize: "8px",
                    width: "8px",
                    height: "8px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(id);
                  }}
                />
              </span>
            );
          })
        )}

        {/* FLÈCHE */}
        <span
          className="position-absolute"
          style={{
            right: "12px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </div>

      {/* DROPDOWN — rendu hors du modal via portal */}
      {open && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default MultiSelect;